import { apiGet, apiSend } from './api.js';
import type {
  PlanDefinition,
  Contract,
  ContractCreate,
  ContractAmendment,
  ContractAmendmentCreate,
  EffectiveEntitlement,
  EntitlementMatrixRow,
  EntitlementImpactPreview,
  DriftCase,
  DriftStatus,
  DriftSeverity,
  B2BInvoice,
  B2BInvoiceCreate,
  B2BInvoiceStatus,
  ProvisioningCommand,
  ProvisioningCommandCreate,
  ProvisioningStatus,
} from '@hospasense/contracts';

// Organisations helper
export async function fetchOrganisationsList(): Promise<
  Array<{ id: string; displayName: string }>
> {
  const data = await apiGet<{ items: Array<{ id: string; displayName: string }> }>(
    '/api/v1/organisations?limit=100',
  );
  return data.items;
}

// Commercial Plans & Contracts
export async function fetchPlans(): Promise<PlanDefinition[]> {
  const data = await apiGet<{ items: PlanDefinition[] }>('/api/v1/commercial/plans');
  return data.items;
}

export async function fetchContracts(params?: {
  organisationId?: string;
  status?: string;
}): Promise<{ items: Contract[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.organisationId) query.set('organisationId', params.organisationId);
  if (params?.status) query.set('status', params.status);
  const qStr = query.toString() ? `?${query.toString()}` : '';
  return apiGet<{ items: Contract[]; total: number }>(`/api/v1/commercial/contracts${qStr}`);
}

export async function fetchContract(id: string): Promise<Contract> {
  return apiGet<Contract>(`/api/v1/commercial/contracts/${id}`);
}

export async function createContract(input: ContractCreate): Promise<Contract> {
  return apiSend<Contract>('POST', '/api/v1/commercial/contracts', input);
}

export async function fetchAmendments(contractId: string): Promise<ContractAmendment[]> {
  const data = await apiGet<{ items: ContractAmendment[] }>(
    `/api/v1/commercial/contracts/${contractId}/amendments`,
  );
  return data.items;
}

export async function addAmendment(
  contractId: string,
  input: ContractAmendmentCreate,
): Promise<ContractAmendment> {
  return apiSend<ContractAmendment>(
    'POST',
    `/api/v1/commercial/contracts/${contractId}/amendments`,
    input,
  );
}

export async function terminateContract(
  contractId: string,
  reason: string,
  expectedVersion: number,
): Promise<Contract> {
  return apiSend<Contract>('POST', `/api/v1/commercial/contracts/${contractId}/terminate`, {
    reason,
    expectedVersion,
  });
}

// Entitlements & Drift
export async function fetchEntitlementMatrix(organisationId: string): Promise<{
  rows: EntitlementMatrixRow[];
  effective: EffectiveEntitlement;
  inGracePeriod: boolean;
  graceUntil: string | null;
}> {
  return apiGet<{
    rows: EntitlementMatrixRow[];
    effective: EffectiveEntitlement;
    inGracePeriod: boolean;
    graceUntil: string | null;
  }>(`/api/v1/entitlements/organisations/${organisationId}`);
}

export async function previewEntitlementImpact(
  organisationId: string,
  proposedModules: string[],
  proposedCapacity: Record<string, number>,
): Promise<EntitlementImpactPreview> {
  return apiSend<EntitlementImpactPreview>(
    'POST',
    `/api/v1/entitlements/organisations/${organisationId}/impact-preview`,
    { proposedModules, proposedCapacity },
  );
}

export async function addEntitlementOverride(
  organisationId: string,
  input: {
    featureOrMetric: string;
    grantType: 'module_grant' | 'capacity_boost' | 'waiver' | 'grace_extension';
    value: unknown;
    reason: string;
    expiresAt: string;
  },
): Promise<EffectiveEntitlement> {
  return apiSend<EffectiveEntitlement>(
    'POST',
    `/api/v1/entitlements/organisations/${organisationId}/overrides`,
    input,
  );
}

export async function fetchDriftCases(params?: {
  organisationId?: string;
  status?: DriftStatus;
  severity?: DriftSeverity;
}): Promise<{ items: DriftCase[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.organisationId) query.set('organisationId', params.organisationId);
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  const qStr = query.toString() ? `?${query.toString()}` : '';
  return apiGet<{ items: DriftCase[]; total: number }>(`/api/v1/entitlements/drift-cases${qStr}`);
}

export async function reconcileDriftCase(
  driftCaseId: string,
  resolutionNote: string,
): Promise<DriftCase> {
  return apiSend<DriftCase>('POST', `/api/v1/entitlements/drift-cases/${driftCaseId}/reconcile`, {
    resolutionNote,
  });
}

// B2B Billing
export async function fetchInvoices(params?: {
  organisationId?: string;
  status?: B2BInvoiceStatus;
}): Promise<{ items: B2BInvoice[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.organisationId) query.set('organisationId', params.organisationId);
  if (params?.status) query.set('status', params.status);
  const qStr = query.toString() ? `?${query.toString()}` : '';
  return apiGet<{ items: B2BInvoice[]; total: number }>(`/api/v1/billing/invoices${qStr}`);
}

export async function fetchInvoice(id: string): Promise<B2BInvoice> {
  return apiGet<B2BInvoice>(`/api/v1/billing/invoices/${id}`);
}

export async function createInvoice(input: B2BInvoiceCreate): Promise<B2BInvoice> {
  return apiSend<B2BInvoice>('POST', '/api/v1/billing/invoices', input);
}

export async function settleInvoice(
  invoiceId: string,
  paymentReference: string,
  expectedVersion: number,
  paidAt?: string,
): Promise<B2BInvoice> {
  return apiSend<B2BInvoice>('POST', `/api/v1/billing/invoices/${invoiceId}/settle`, {
    paymentReference,
    expectedVersion,
    paidAt,
  });
}

export async function voidInvoice(
  invoiceId: string,
  voidReason: string,
  expectedVersion: number,
): Promise<B2BInvoice> {
  return apiSend<B2BInvoice>('POST', `/api/v1/billing/invoices/${invoiceId}/void`, {
    voidReason,
    expectedVersion,
  });
}

// Provisioning
export async function fetchProvisioningCommands(params?: {
  organisationId?: string;
  status?: ProvisioningStatus;
}): Promise<{ items: ProvisioningCommand[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.organisationId) query.set('organisationId', params.organisationId);
  if (params?.status) query.set('status', params.status);
  const qStr = query.toString() ? `?${query.toString()}` : '';
  return apiGet<{ items: ProvisioningCommand[]; total: number }>(
    `/api/v1/provisioning/commands${qStr}`,
  );
}

export async function queueProvisioningCommand(
  input: ProvisioningCommandCreate,
): Promise<ProvisioningCommand> {
  return apiSend<ProvisioningCommand>('POST', '/api/v1/provisioning/commands', input);
}
